import { useState, useCallback, useRef } from 'react';

const useNotifications = () => {
  const [notification, setNotification] = useState(null);
  
  // Stable onClose callback that won't change between renders
  const hideNotificationCallback = useCallback(() => {
    setNotification(null);
  }, []);

  const showNotification = useCallback((notificationData) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'success', // Default to success since you only want success notifications
      title: '',
      message: '',
      showCloseButton: true,
      iconSize: 'default',
      variant: 'default',
      ...notificationData,
      // Ensure auto-close is enabled by default (unless explicitly disabled)
      // This ensures all notifications auto-close after 5 seconds
      autoClose: notificationData.autoClose !== false,
      autoCloseDelay: notificationData.autoCloseDelay || 5000,
      // Use stable callback to prevent useEffect from resetting in Notification component
      onClose: notificationData.onClose || hideNotificationCallback
    };
    
    setNotification(newNotification);
    return id;
  }, [hideNotificationCallback]);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const showSuccess = useCallback((title, message) => {
    return showNotification({ type: 'success', title, message });
  }, [showNotification]);

  const showError = useCallback((title, message) => {
    return showNotification({ type: 'error', title, message });
  }, [showNotification]);

  const showWarning = useCallback((title, message) => {
    return showNotification({ type: 'warning', title, message });
  }, [showNotification]);

  const showInfo = useCallback((title, message) => {
    return showNotification({ type: 'info', title, message });
  }, [showNotification]);

  return {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

export default useNotifications;
